/**
 * Компонент статуса синхронизации - Enterprise Design v2.0
 * QRIUM Recvisit
 */
import { useState, useEffect, memo, useCallback } from 'react'
import { EventsRepo } from '../offline/eventsRepo'
import { SyncService } from '../services/syncService'
import { LogsRepo } from '../offline/logsRepo'

export const SyncStatus = memo(function SyncStatus() {
  const [unsyncedCount, setUnsyncedCount] = useState(0)
  const [failedCount, setFailedCount] = useState(0)
  const [syncedCount, setSyncedCount] = useState(0)
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncErrors, setSyncErrors] = useState<string[]>([])
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  const loadStatus = useCallback(async () => {
    try {
      const unsynced = await EventsRepo.getUnsynced(1000)
      const failed = await EventsRepo.getFailed(1000)
      const allEvents = await EventsRepo.getAll({ limit: 10000 })
      const synced = allEvents.filter((e) => e.synced)
      
      setUnsyncedCount(unsynced.length)
      setFailedCount(failed.length)
      setSyncedCount(synced.length)

      const logs = await LogsRepo.getAll({ limit: 100 })
      const syncLog = logs.find((log) => 
        log.message.includes('Synced') || 
        log.message.includes('Sync completed') ||
        log.message.includes('events,')
      )
      if (syncLog) {
        setLastSyncTime(syncLog.timestamp)
      }

      const errorLogs = logs
        .filter((log) => log.level === 'error' && log.message.includes('sync'))
        .slice(0, 3)
        .map((log) => `${new Date(log.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}: ${log.message.substring(0, 50)}`)
      setSyncErrors(errorLogs)
    } catch (error) {
      console.error('Failed to load sync status:', error)
    }
  }, [])

  useEffect(() => {
    loadStatus()
    const interval = setInterval(loadStatus, 5000)
    
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      clearInterval(interval)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [loadStatus])

  const handleSyncNow = async () => {
    setIsSyncing(true)
    try {
      await SyncService.syncNow()
      await loadStatus()
    } catch (error) {
      console.error('Manual sync failed:', error)
    } finally {
      setIsSyncing(false)
    }
  }

  const handleRetryFailed = async () => {
    setIsSyncing(true)
    try {
      const failed = await EventsRepo.getFailed(100)
      for (const event of failed) {
        await EventsRepo.resetFailedStatus(event.id)
      }
      await SyncService.syncNow()
      await loadStatus()
    } catch (error) {
      console.error('Retry failed:', error)
    } finally {
      setIsSyncing(false)
    }
  }

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const totalPending = unsyncedCount + failedCount

  return (
    <div className="space-y-section">
      {/* Network Status Banner */}
      <div className={`focus-block-elevated ${!isOnline ? 'border-[#f59e0b]/30' : ''}`}>
        <div className="flex items-center gap-4">
          <div className={`
            w-14 h-14 rounded-2xl flex items-center justify-center text-2xl
            ${isOnline ? 'bg-[#22c55e]/15' : 'bg-[#f59e0b]/15'}
          `}>
            {isOnline ? '🌐' : '📴'}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-[18px] font-semibold ${isOnline ? 'text-[#22c55e]' : 'text-[#f59e0b]'}`}>
              {isOnline ? 'Онлайн' : 'Офлайн'}
            </p>
            <p className="text-[13px] text-[#a0a0a0] mt-0.5">
              {isOnline ? 'Синхронизация доступна' : 'Данные сохраняются локально'}
            </p>
          </div>
          <button 
            onClick={loadStatus} 
            disabled={isSyncing} 
            className="btn btn-md btn-secondary w-11 h-11 p-0 rounded-xl"
          >
            🔄
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid-4">
        <div className="stat-card">
          <div className="stat-card-value text-[#22c55e]">{syncedCount}</div>
          <div className="stat-card-label">Синхр.</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value text-[#f59e0b]">{unsyncedCount}</div>
          <div className="stat-card-label">Ожидает</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value text-[#ef4444]">{failedCount}</div>
          <div className="stat-card-label">Ошибки</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value-medium text-[#3b82f6]">
            {lastSyncTime ? formatTime(lastSyncTime).split(' ')[1] : '—'}
          </div>
          <div className="stat-card-label">Посл. синхр.</div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleSyncNow}
          disabled={isSyncing || !isOnline}
          className="btn btn-lg btn-primary btn-full"
        >
          {isSyncing ? (
            <>
              <div className="loading-spinner w-5 h-5 border-2" />
              <span>Синхронизация...</span>
            </>
          ) : (
            <>
              <span>Синхронизировать сейчас</span>
              {totalPending > 0 && (
                <span className="bg-white/20 px-2.5 py-0.5 rounded-full text-[12px] font-semibold ml-2">
                  {totalPending}
                </span>
              )}
            </>
          )}
        </button>

        {failedCount > 0 && (
          <button
            onClick={handleRetryFailed}
            disabled={isSyncing}
            className="btn btn-lg btn-warning btn-full"
          >
            Повторить неудачные ({failedCount})
          </button>
        )}
      </div>

      {/* Info Card */}
      <div className="info-card">
        <div className="info-card-icon">ℹ️</div>
        <div className="flex-1">
          <p className="text-white font-semibold text-[14px] mb-1">Автоматическая синхронизация</p>
          <p className="text-[13px] text-[#a0a0a0] leading-relaxed">
            События синхронизируются каждые 60 секунд при наличии интернета. 
            Без связи данные сохраняются локально.
          </p>
        </div>
      </div>

      {/* Error Log */}
      {syncErrors.length > 0 && (
        <div className="focus-block bg-[#ef4444]/5 border border-[#ef4444]/20">
          <div className="flex items-center justify-between mb-3">
            <span className="section-title text-[#ef4444]">Последние ошибки</span>
            <span className="text-[11px] text-[#ef4444]/60">{syncErrors.length} записей</span>
          </div>
          <ul className="space-y-2">
            {syncErrors.map((error, index) => (
              <li key={index} className="text-[13px] text-[#ef4444]/80 leading-relaxed">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
})
